import { Box, IconButton, Stack, Typography, Button, alpha, useTheme } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import { GustavoBurbuja } from './GustavoBurbuja';
import type { Anuncio } from '../../store/api/anunciosApi';

/**
 * ANUNCIO EN FORMA DE NOTIFICACIÓN — la tarjeta de la esquina.
 *
 * La otra forma es la lámina a pantalla completa (`AvisoLamina`), que frena todo. Esta no
 * frena: acompaña. Es para lo que hay que ver sin urgencia —una función nueva, un curso, un
 * plazo que vence en tres semanas— donde una pared antes de empezar a trabajar hace que se
 * cierre sin leer.
 *
 * ⛔ NO SE VA SOLA, y eso es el punto. Un toast que se desvanece a los cinco segundos se
 * pierde justo cuando el agente estaba mirando otra cosa; y como vive en el layout, cambiar
 * de pantalla no la desmonta. Se queda ahí, de pantalla en pantalla, hasta que la persona la
 * cierra con la X o entra al link. Recién ahí se marca vista y no vuelve nunca más.
 *
 * Por eso no lleva `autoHideDuration` ni cierre por clic afuera: las dos cosas la harían
 * desaparecer sin que nadie decidiera nada, que es exactamente lo que se quiso evitar.
 */
export function AvisoNotificacion({ aviso, onCerrar }: {
  aviso: Anuncio;
  /** `(abrirGustavo, irA)` — mismo contrato que la lámina, así el gate no distingue. */
  onCerrar: (abrirGustavo: boolean, irA?: string) => void;
}) {
  const theme = useTheme();
  const acento = theme.palette.primary.main;
  const cta = aviso.cta;
  const irA = cta?.href ?? undefined;
  const abreGustavo = cta?.accion === 'gustavo';

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        // Arriba del botón flotante de Gustavo, que vive abajo a la derecha.
        right: { xs: 12, sm: 20 },
        bottom: { xs: 12, sm: 96 },
        zIndex: (t) => t.zIndex.snackbar,
        width: { xs: 'calc(100vw - 24px)', sm: 380 },
        maxWidth: 380,
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(acento, 0.35)}`,
        boxShadow: `0 18px 48px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha(acento, 0.08)}`,
        // Entra desde abajo una sola vez: llama la atención sin robar el foco.
        animation: 'aviso-notif-entra .42s cubic-bezier(.16,1,.3,1)',
        '@keyframes aviso-notif-entra': {
          from: { opacity: 0, transform: 'translateY(18px) scale(.98)' },
          to:   { opacity: 1, transform: 'none' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* Filo de color arriba: es lo que la hace leer como "algo nuevo" y no como un cartel. */}
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${acento}, ${alpha(acento, 0.15)})` }} />

      <Stack direction="row" spacing={1.5} sx={{ p: 1.75, pr: 1.25 }}>
        <Box sx={{ flexShrink: 0, pt: 0.25 }}>
          {aviso.figura === 'gustavo'
            ? <GustavoBurbuja size={44} />
            : aviso.figuraUrl
              ? <Box component="img" src={aviso.figuraUrl} alt="" sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover' }} />
              : (
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(acento, 0.14), color: acento,
                }}>
                  <CampaignRounded fontSize="small" />
                </Box>
              )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {aviso.eyebrow && (
            <Typography sx={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase',
              color: acento, mb: 0.25,
            }}>
              {aviso.eyebrow}
            </Typography>
          )}
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}>
            {aviso.titulo}
          </Typography>
          {aviso.bajada && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45, mt: 0.5 }}>
              {aviso.bajada}
            </Typography>
          )}

          {/* Los puntos de la lámina entran hasta 2: más que eso ya no es una notificación. */}
          {aviso.puntos?.length > 0 && (
            <Stack component="ul" sx={{ m: 0, mt: 0.75, pl: 2, gap: 0.25 }}>
              {aviso.puntos.slice(0, 2).map((p) => (
                <Typography key={p} component="li" sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>{p}</Typography>
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
            {cta?.texto && (
              <Button
                size="small"
                variant="contained"
                onClick={() => onCerrar(abreGustavo, irA)}
                sx={{ fontSize: 12.5, py: 0.4, px: 1.5, textTransform: 'none', fontWeight: 700 }}
              >
                {cta.texto}
              </Button>
            )}
            <Button
              size="small"
              color="inherit"
              onClick={() => onCerrar(false)}
              sx={{ fontSize: 12.5, py: 0.4, textTransform: 'none', color: 'text.disabled' }}
            >
              {aviso.cerrar || 'Después'}
            </Button>
          </Stack>
        </Box>

        <IconButton
          size="small"
          aria-label="Cerrar el anuncio"
          onClick={() => onCerrar(false)}
          sx={{ alignSelf: 'flex-start', color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
